import { useCallback, useEffect, useMemo, useState } from "react"

import { useNavigate, useParams } from "react-router-dom"
import { Button, Container, type ContainerProps, Divider, Message, Segment } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { RichText } from "simplydo/react/richtext"

import api from "api"
import Login from "components/entry/Login"
import { PlaceholderItem, PlaceholderStack } from "components/lib/UI"
import { useAppSelector } from "store"
import util from "utils/utils"

type CustomPageProps = {
  pageId?: string
  isPreview?: boolean
  containerProps?: ContainerProps
}

type PageResponse = OpenAPI.GET<"/pages/{page_id}">["response"]

const CustomPage = ({
  pageId: propPageId,
  isPreview = false,
  containerProps = {
    style: {
      paddingTop: 20,
      paddingBottom: 20,
    },
  },
}: CustomPageProps) => {
  const urlOrganisation: OpenAPI.Schemas["Organisation"] = useAppSelector(
    (state) => state.organisations?.urlOrganisation,
  )
  const user = useAppSelector((state) => state.user)
  const navigate = useNavigate()

  const { pageId: urlPageId } = useParams<{ pageId: string }>()
  const pageId = useMemo(() => propPageId ?? urlPageId, [propPageId, urlPageId])

  const [page, setPage] = useState<PageResponse["page"] | null>(null)
  const [loading, setLoading] = useState(true)
  const [didFail, setDidFail] = useState(false)
  const [tryAuth, setTryAuth] = useState(false)

  const getPage = useCallback(() => {
    setDidFail(false)
    api.pages.get(
      pageId,
      (data: PageResponse) => {
        setPage(data.page)
        setLoading(false)
      },
      (err) => {
        if (err?.status === 403) {
          setTryAuth(true)
        }
        setDidFail(true)
        setLoading(false)
      },
    )
  }, [pageId])

  useEffect(() => {
    getPage()
  }, [getPage])

  const navigateToEditPage = useCallback(
    (id: string) => {
      if (!page) return
      let path = ""
      if (page.forType === "group") {
        path = `/groups/${page.forId}/settings/pages/edit/${id}`
      } else if (page.forType === "organisation") {
        path = `/admin/communication/pages/edit/${id}`
      }
      if (path) navigate(path)
    },
    [navigate, page],
  )

  const canManageGroupPages = useMemo(
    () =>
      util.hasPermission(user, "group.managePages", page?.forId) ||
      util.canManageGroups(user, user?.ownerOrganisation?._id),
    [user, page],
  )
  const canManageOrgPages = useMemo(
    () => util.hasPermission(user, "org.viewDashboard", user?.ownerOrganisation?._id),
    [user],
  )

  if (!loading && didFail && tryAuth && !user) {
    return (
      <Container {...containerProps}>
        <Segment style={{ maxWidth: 700, width: 700, alignSelf: "center", padding: 50 }}>
          <Message info style={{ alignSelf: "center", marginBottom: 20 }}>
            This content is restricted to registered users, please log in to view this page.
          </Message>
          {/* @ts-ignore */}
          <Login embedded />
        </Segment>
      </Container>
    )
  }
  if (!page) return null
  return (
    <Container {...containerProps}>
      {!loading && didFail ? (
        <Message error>
          <Message.Header>You are unable to access this page</Message.Header>
          <Message.Content>
            This page has been restricted to certain viewers only. Please contact an admin if you think this is in error
          </Message.Content>
        </Message>
      ) : (
        <>
          {loading ? (
            <PlaceholderStack gap={10} style={{ marginBottom: 20 }}>
              <PlaceholderItem height={30} />
            </PlaceholderStack>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h1 style={{ margin: 0 }}>{page?.title ?? "New page"}</h1>
              {!isPreview &&
              ((page.forType === "group" && canManageGroupPages) ||
                (page?.forType === "organisation" && canManageOrgPages)) ? (
                <Button
                  icon="edit"
                  size="small"
                  content="Edit page"
                  onClick={() => navigateToEditPage(page._id)}
                  style={{ marginLeft: "auto" }}
                />
              ) : null}
            </div>
          )}

          <Divider />

          {loading ? (
            <PlaceholderStack gap={10} style={{ marginBottom: 30 }}>
              <PlaceholderItem height={30} fluid />
              <PlaceholderItem height={30} fluid />
              <PlaceholderItem height={30} fluid />
              <PlaceholderItem height={30} fluid />
              <PlaceholderItem height={30} fluid />
            </PlaceholderStack>
          ) : (
            <RichText
              readOnly
              value={page?.content ?? ""}
              forType="organisation"
              forId={user ? user?.ownerOrganisation?._id : urlOrganisation?._id}
            />
          )}
        </>
      )}
    </Container>
  )
}

export default CustomPage
